"use client";

import { useMemo, useState } from "react";
import { Check, GitCompareArrows } from "lucide-react";
import { CompareModal } from "@/components/site/CompareModal";
import { useCompareShortlist } from "@/hooks/useCompareShortlist";
import { useResolvedCompareProviders } from "@/hooks/useResolvedCompareProviders";
import { cn } from "@/lib/utils";
import type { ProvidersApiResponse } from "@/app/api/providers/route";

type ProviderCard = ProvidersApiResponse["providers"][number];

type ProviderDetailCompareClientProps = {
  provider: ProviderCard;
};

export function ProviderDetailCompareClient({
  provider,
}: ProviderDetailCompareClientProps) {
  const shortlist = useCompareShortlist();
  const [compareOpen, setCompareOpen] = useState(false);
  const localCompareProviders = useMemo(() => [provider], [provider]);
  const compareProviders = useResolvedCompareProviders(
    shortlist.slugs,
    localCompareProviders,
  );
  const added = shortlist.isAdded(provider.slug);
  const canAdd = added || !shortlist.isFull;
  const hasShortlist = shortlist.slugs.length > 0;
  const helperText = added
    ? "This provider is in your comparison."
    : shortlist.isFull
      ? "Compare is full. Remove a provider before adding another."
      : "Compare up to 4 providers.";

  function toggleProvider() {
    shortlist.toggle(provider.slug);
  }

  return (
    <>
      <div className="rounded-2xl border border-[var(--color-border)] bg-slate-50 p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="section-label-muted">Compare providers</p>
            <p className="mt-1 text-sm text-[var(--color-muted)]">
              {helperText}
            </p>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={toggleProvider}
              disabled={!canAdd}
              aria-pressed={added}
              className={cn(
                "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700",
                added
                  ? "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  : canAdd
                    ? "bg-emerald-700 text-white hover:bg-emerald-800"
                    : "cursor-not-allowed border border-[var(--color-border)] bg-white text-[var(--color-muted)]",
              )}
            >
              {added ? (
                <Check size={16} aria-hidden="true" />
              ) : (
                <GitCompareArrows size={16} aria-hidden="true" />
              )}
              {added ? "Added to compare" : "Add to compare"}
            </button>
            {hasShortlist && (
              <button
                type="button"
                onClick={() => setCompareOpen(true)}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-[var(--color-border)] bg-white px-4 py-2 text-sm font-semibold text-[var(--color-primary)] transition-colors hover:border-[var(--color-accent)]"
              >
                <GitCompareArrows size={16} aria-hidden="true" />
                View compare ({shortlist.slugs.length})
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--color-border)] bg-white/95 p-3 shadow-[0_-8px_24px_rgba(28,28,30,0.08)] backdrop-blur sm:hidden">
        <div className="mx-auto flex max-w-md gap-2">
          <button
            type="button"
            onClick={toggleProvider}
            disabled={!canAdd}
            aria-pressed={added}
            className={cn(
              "inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors",
              added
                ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                : canAdd
                  ? "bg-emerald-700 text-white"
                  : "cursor-not-allowed border border-[var(--color-border)] bg-white text-[var(--color-muted)]",
            )}
          >
            {added ? (
              <Check size={16} aria-hidden="true" />
            ) : (
              <GitCompareArrows size={16} aria-hidden="true" />
            )}
            {added ? "Added" : "Add to compare"}
          </button>
          {hasShortlist && (
            <button
              type="button"
              onClick={() => setCompareOpen(true)}
              aria-label={`View ${shortlist.slugs.length} selected providers`}
              className="inline-flex min-h-11 items-center justify-center rounded-xl border border-[var(--color-border)] bg-white px-4 text-sm font-semibold text-[var(--color-primary)]"
            >
              <GitCompareArrows size={17} aria-hidden="true" />
              <span className="ml-2">{shortlist.slugs.length}</span>
            </button>
          )}
        </div>
      </div>

      <CompareModal
        open={compareOpen}
        providers={compareProviders}
        onClose={() => setCompareOpen(false)}
        onRemove={(slug) => shortlist.toggle(slug)}
        onClearAll={() => {
          shortlist.clearAll();
          setCompareOpen(false);
        }}
      />
    </>
  );
}
